import {
  AutoUpdateTimeout,
  LiveBundleIdSeparator,
  MIN_SUPPORTED_CHROME_VERSION,
  ScrollThreshold,
} from "@/constants/global";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useAppUpdateStore } from "@/stores/core/appUpdateStore";
import { useIonModalStore } from "@/stores/core/ionModalStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { IonFabButtonMenuItem } from "@/types/global";
import {
  contractOutline,
  desktopOutline,
  expandOutline,
  phoneLandscape,
  phonePortrait,
} from "ionicons/icons";
import { defineStore, getActivePinia, storeToRefs } from "pinia";
import { onUnmounted, ref, watch } from "vue";

export async function setDeviceControlsMenuItems() {
  const appSettingsStore = useAppSettingsStore();
  const {
    ShowDeviceControls,
    DeviceControls,
    IsAppFullscreen,
    AppScreenOrientation,
    IsNativePlatform,
  } = storeToRefs(appSettingsStore);
  const { toggleAppScreenOrientation, toggleAppFullscreen, onEnableCastingClicked } =
    appSettingsStore;
  const fabMenuItems: Array<IonFabButtonMenuItem> = [];
  const rotateScreenIcon = AppScreenOrientation.value.startsWith("portrait")
    ? phonePortrait
    : phoneLandscape;
  if (!ShowDeviceControls.value) {
    return [];
  }
  if (DeviceControls.value.AllowScreenRotate && IsNativePlatform.value) {
    fabMenuItems.push({
      Id: 1,
      Icon: rotateScreenIcon,
      Label: "Rotate",
      onClick: async () => await toggleAppScreenOrientation(),
    });
  }
  if (DeviceControls.value.AllowFullscreen) {
    fabMenuItems.push({
      Id: 2,
      Icon: IsAppFullscreen.value ? contractOutline : expandOutline,
      Label: "Fullscreen",
      onClick: toggleAppFullscreen,
    });
  }
  // Casting hands off to the OS settings app — there is nothing to open on web.
  if (DeviceControls.value.AllowScreenCast && IsNativePlatform.value) {
    fabMenuItems.push({
      Id: 3,
      Icon: desktopOutline,
      Label: "Casting",
      onClick: onEnableCastingClicked,
    });
  }
  return fabMenuItems;
}

export async function handleAppUpdates() {
  const appSettingsStore = useAppSettingsStore();
  const { AppUpdateRequired, SkippedAppVersion, LatestAppVersion, IsAutoLiveUpdateExhausted } =
    storeToRefs(appSettingsStore);
  if (!AppUpdateRequired.value) {
    return;
  }
  if (SkippedAppVersion.value === LatestAppVersion.value?.AppVersion) {
    return;
  }
  const appUpdateStore = useAppUpdateStore();
  const { IsLiveUpdating } = storeToRefs(appUpdateStore);
  await appUpdateStore.initializeAppUpdate();
  // The attempt cap is persisted and keyed to the offered version, so it survives the restart
  // the auto update performs. Past it, stop restarting the app and fall through to the modal —
  // which is dismissable, and is the only way a user in a failing update can reach the app.
  if (IsLiveUpdating.value && !IsAutoLiveUpdateExhausted.value) {
    const sessionStore = useSessionStore();
    const { UpdateProgress } = storeToRefs(appUpdateStore);
    // This await suspends the navigation, and the splash was hidden one step earlier in the router
    // guard by afterNavigateValidated() — so without re-showing it the bundle downloads behind a
    // blank (cold start) or frozen (in-app) screen for up to AutoUpdateTimeout, with no sign that
    // anything is happening. The splash text is the only progress surface here; ModalAppUpdate,
    // which owns the progress bar, is not open yet.
    sessionStore.showAppLoader();
    // Sync flush so the percentage lands in the DOM as each progress event arrives, and never
    // after autoAppLiveUpdate has moved the text on to "Restarting app...".
    const stopProgressWatch = watch(
      UpdateProgress,
      (progress) => updateLoaderText(`Downloading latest app... ${progress}%`),
      { flush: "sync" },
    );
    const liveUpdatePromise = appUpdateStore.autoAppLiveUpdate();
    const timeoutPromise = new Promise<void>((resolve) => setTimeout(resolve, AutoUpdateTimeout));
    await Promise.race([liveUpdatePromise, timeoutPromise]);
    stopProgressWatch();
    // Either the app is reloading onto the new bundle, or the timeout won and ModalAppUpdate takes
    // over the progress display below — the full-screen splash must not sit on top of that modal.
    sessionStore.hideAppLoader();
  }
  if (!AppUpdateRequired.value) {
    return;
  }
  const { default: ModalAppUpdate } = await import("@/components/modals/shared/ModalAppUpdate.vue");
  const ionModalStore = useIonModalStore();
  const { openInlineModal } = ionModalStore;
  const modalProps = { CustomCssClass: "app-update-modal" };
  await openInlineModal(ModalAppUpdate, {}, modalProps);
}

export function useNavbarScrollHandler(
  scrollThreshold: number = ScrollThreshold,
  lockDuration: number = 300,
) {
  const appSettingsStore = useAppSettingsStore();
  const { IsTabletScreen, HideNavbarOnScroll } = storeToRefs(appSettingsStore);
  const { triggerScrollCallbacks } = appSettingsStore;
  const previousScrollTop = ref(0);
  const isTogglingNavbar = ref(false);

  let navbarToggleTimeout: NodeJS.Timeout | null = null;

  function lockNavbarToggle(): void {
    if (navbarToggleTimeout) {
      clearTimeout(navbarToggleTimeout);
    }
    isTogglingNavbar.value = true;
    navbarToggleTimeout = setTimeout(() => {
      isTogglingNavbar.value = false;
    }, lockDuration);
  }

  function handleScroll(event: any) {
    if (isTogglingNavbar.value) {
      return;
    }
    const scrollTop = event.detail.scrollTop;
    if (scrollTop < 0) {
      return;
    }
    const delta = scrollTop - previousScrollTop.value;
    if (IsTabletScreen.value) {
      if (delta < 0 && HideNavbarOnScroll.value) {
        HideNavbarOnScroll.value = false;
        lockNavbarToggle();
      } else if (delta > 0 && Math.abs(scrollTop) > scrollThreshold && !HideNavbarOnScroll.value) {
        HideNavbarOnScroll.value = true;
        lockNavbarToggle();
      }
    }
    previousScrollTop.value = scrollTop;
    triggerScrollCallbacks(event);
  }

  onUnmounted(() => {
    if (navbarToggleTimeout) {
      clearTimeout(navbarToggleTimeout);
      navbarToggleTimeout = null;
    }
  });

  return { handleScroll };
}

// A dotted numeric version, or null when the string is not one. Rejecting is the whole point:
// the previous implementation ran every segment through Number(), so "v9.0.5", "9.0.5-hotfix" or
// a stray space produced NaN, every comparison against NaN was false, and the caller fell through
// to "outdated". That verdict is permanent — no release can clear it — and it drives the auto
// live update, so a single malformed value in AppVersionInfo would put every install into a
// download/restart cycle. Callers must decide what an unreadable version means for them; see
// isValidAppVersion.
function parseAppVersionParts(version: string): Array<number> | null {
  const parts = version.trim().split(".");
  const versionParts: Array<number> = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) {
      return null;
    }
    versionParts.push(Number(part));
  }
  return versionParts;
}

export function isValidAppVersion(version: string | null | undefined): boolean {
  return typeof version === "string" && parseAppVersionParts(version) !== null;
}

// Segment-by-segment, missing trailing segments read as 0 ("9.0" === "9.0.0"). This replaces a
// base-100 weighted sum that silently collided once any segment reached 100 — "9.0.100" and
// "9.1" scored identically — which on the wrong pair of versions is another way to be
// permanently out of date. An unreadable version on either side compares EQUAL, so callers fail
// closed: no update is demanded on the strength of a value we cannot read.
export function compareAppVersions(v1: string, v2: string): number {
  const versionParts1 = parseAppVersionParts(v1);
  const versionParts2 = parseAppVersionParts(v2);
  if (versionParts1 === null || versionParts2 === null) {
    return 0;
  }
  const segmentCount = Math.max(versionParts1.length, versionParts2.length);
  for (let i = 0; i < segmentCount; i++) {
    const segment1 = versionParts1[i] ?? 0;
    const segment2 = versionParts2[i] ?? 0;
    if (segment1 !== segment2) {
      return segment1 > segment2 ? 1 : -1;
    }
  }
  return 0;
}

export function isAppVersionOutdated(clientVersion: string, minVersion: string): boolean {
  return compareAppVersions(clientVersion, minVersion) < 0;
}

export function buildLiveBundleId(appVersion: string, minNativeVersion: string): string {
  return `${appVersion}${LiveBundleIdSeparator}${minNativeVersion}`;
}

export function parseLiveBundleId(bundleId: string): {
  appVersion: string;
  minNativeVersion: string | null;
} {
  const separatorIndex = bundleId.indexOf(LiveBundleIdSeparator);
  if (separatorIndex === -1) {
    return { appVersion: bundleId, minNativeVersion: null };
  }
  return {
    appVersion: bundleId.substring(0, separatorIndex),
    minNativeVersion: bundleId.substring(separatorIndex + LiveBundleIdSeparator.length),
  };
}

// The bundle is compiled for Chrome 111 (see `build.target` in vite.config.js). Windows 7 stops
// receiving Chrome updates at 109, so those machines run below that baseline permanently and can
// fail in ways no amount of retrying fixes. Reported only so the failure screen can say so
// plainly instead of blaming the servers.
//
// Native builds are exempt: the Android WebView reports its own Chromium version in the same
// `Chrome/` token, and an old WebView is a different problem with a different fix. A browser with
// no `Chrome/` token (Firefox, Safari) is left alone rather than guessed at.
export function getUnsupportedBrowserLabel(): string | null {
  if (useAppSettingsStore().IsNativePlatform) {
    return null;
  }
  const chromeMajorVersion = /Chrome\/(\d+)/.exec(navigator.userAgent)?.[1];
  if (chromeMajorVersion === undefined) {
    return null;
  }
  const majorVersion = Number(chromeMajorVersion);
  return majorVersion < MIN_SUPPORTED_CHROME_VERSION ? `Chrome ${majorVersion}` : null;
}

export function updateLoaderText(text: string) {
  const loaderText = document.getElementById("app-loader-text");
  if (loaderText) loaderText.textContent = text;
}

export async function resetPiniaStores(isSystemExpired: boolean) {
  const excludedStores = ["appSettingsStore", "sessionStore", "apiServicesStore"];
  const appSettingsStore = useAppSettingsStore();
  const sessionStore = useSessionStore();
  const { UserInfo } = storeToRefs(sessionStore);
  const activePinia = getActivePinia();
  if (activePinia) {
    Object.entries(activePinia.state.value).forEach(([storeName, state]) => {
      if (!excludedStores.includes(storeName)) {
        const storeDefinition = defineStore(storeName, state);
        const store = storeDefinition(activePinia);
        store.$reset();
      }
    });
  }
  const apiServicesStore = useApiServicesStore();
  const { unverifiedUserApi } = storeToRefs(apiServicesStore);
  await unverifiedUserApi.value.expireLoginSession(UserInfo.value.LoginSessionId, isSystemExpired);
  await sessionStore.resetStore();
  appSettingsStore.resetMenus();
  apiServicesStore.$reset();
}
